<style>
@import "@/assets/css/custom.css";

.purchase-table-responsive {
	overflow-x: auto;
	overflow-y: visible;
}
</style>

<template>
	<div>
		<Breadcrumbs main="Accounts" title="Purchase" />

		<div class="container-fluid">
			<div class="card">
				<div class="card-body">
					<form @submit.prevent="submit">
						<div class="row">
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Product type<span class="text-danger">*</span></label>
								<select class="form-select" v-model="form.product_type" @change="onProductTypeChange">
									<option value="">— Select —</option>
									<option value="1">Raw Material</option>
									<option value="2">GPS</option>
									<option value="3">SIM</option>
									<option value="4">Camera</option>
									<option value="5">Speed Governer</option>
									<option value="6">Other</option>
									<option value="7">Board</option>
								</select>
								<div v-if="errors.product_type" class="text-danger small mt-1">{{ errors.product_type }}</div>
							</div>

							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Bank account</label>
								<select class="form-select" v-model="form.bank_account_id">
									<option value="">— Select bank —</option>
									<option v-for="b in bankAccounts" :key="b.id" :value="String(b.id)">
										{{ b.bank_name }} — {{ b.account_number }}
									</option>
								</select>
								<div v-if="errors.bank_account_id" class="text-danger small mt-1">{{ errors.bank_account_id }}</div>
							</div>

							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Payment date</label>
								<input class="form-control" type="date" v-model="form.payment_date" />
								<div v-if="errors.payment_date" class="text-danger small mt-1">{{ errors.payment_date }}</div>
							</div>
						</div>

						<div class="row">
							<div v-if="form.product_type !== '7'" class="col-md-4 form-group mt-2">
								<label class="control-label">Supplier</label>
								<input
									type="text"
									class="form-control"
									v-model="supplierSearch"
									placeholder="Search supplier (optional)…"
									@focus="openSupplierDropdown($event)"
									@input="openSupplierDropdown($event)"
									@blur="scheduleCloseSupplierDropdown"
								/>
								<small v-if="form.supplier_id" class="text-muted d-block mt-1">
									Selected: {{ supplierLabelById(form.supplier_id) }}
									<a href="#" class="ms-2 text-danger" @click.prevent="clearSupplier">clear</a>
								</small>
								<div v-if="errors.supplier_id" class="text-danger small mt-1">{{ errors.supplier_id }}</div>
							</div>

							<div v-else class="col-md-4 form-group mt-2">
								<label class="control-label">Vendor<span class="text-danger">*</span></label>
								<input
									type="text"
									class="form-control"
									v-model="vendorSearch"
									placeholder="Search vendor…"
									@focus="openVendorDropdown($event)"
									@input="openVendorDropdown($event)"
									@blur="scheduleCloseVendorDropdown"
								/>
								<small v-if="form.vendor_id" class="text-muted d-block mt-1">
									Selected: {{ vendorLabelById(form.vendor_id) }}
									<a href="#" class="ms-2 text-danger" @click.prevent="clearVendor">clear</a>
								</small>
								<div v-if="errors.vendor_id" class="text-danger small mt-1">{{ errors.vendor_id }}</div>
							</div>

							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Invoice number</label>
								<input class="form-control" type="text" v-model="form.invoice_number" placeholder="Optional" />
								<div v-if="errors.invoice_number" class="text-danger small mt-1">{{ errors.invoice_number }}</div>
							</div>

							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Invoice date</label>
								<input class="form-control" type="date" v-model="form.invoice_date" />
								<div v-if="errors.invoice_date" class="text-danger small mt-1">{{ errors.invoice_date }}</div>
							</div>
						</div>

						<div class="row">
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Payment / transaction ID</label>
								<input class="form-control" type="text" v-model="form.payment_id" placeholder="UTR / reference" />
								<div v-if="errors.payment_id" class="text-danger small mt-1">{{ errors.payment_id }}</div>
							</div>
						</div>

						<div class="row">
							<div class="col-md-6 form-group mt-2">
								<label class="control-label">Paid to name</label>
								<input class="form-control" type="text" v-model="form.user_name" placeholder="Optional" />
								<div v-if="errors.user_name" class="text-danger small mt-1">{{ errors.user_name }}</div>
							</div>

							<div class="col-md-6 form-group mt-2">
								<label class="control-label">Paid to mobile</label>
								<input class="form-control" v-model="form.user_mobile" v-mobile-limit placeholder="Optional" maxlength="10" minlength="10" />
								<div v-if="errors.user_mobile" class="text-danger small mt-1">{{ errors.user_mobile }}</div>
							</div>
						</div>

						<!-- Raw materials -->
						<div v-if="form.product_type === '1'" class="mt-4">
							<div class="d-flex justify-content-between align-items-center border-bottom pb-2">
								<h6 class="mb-0">Raw materials</h6>
								<button type="button" class="btn btn-sm btn-primary" @click="addMaterialRow">Add material</button>
							</div>

							<div class="table-responsive mt-3 purchase-table-responsive">
								<table class="table table-sm table-bordered align-middle">
									<thead class="table-light">
										<tr align="center">
											<th style="min-width: 280px">Material</th>
											<th style="min-width: 120px">Quantity</th>
											<th style="min-width: 160px">Amount (₹)</th>
											<th style="width: 80px">Remove</th>
										</tr>
									</thead>
									<tbody v-if="materialRows.length">
										<tr v-for="(row, idx) in materialRows" :key="'mr-' + idx" align="center">
											<td class="text-start">
												<input
													type="text"
													class="form-control form-control-sm"
													v-model="row.material_search"
													placeholder="Search material…"
													@focus="openMaterialDropdown(idx, $event)"
													@input="openMaterialDropdown(idx, $event)"
													@blur="scheduleCloseMaterialDropdown"
												/>
												<small v-if="row.material_id" class="text-muted d-block mt-1">
													Selected: {{ materialLabelById(row.material_id) }}
												</small>
											</td>
											<td>
												<input
													class="form-control form-control-sm"
													type="number"
													min="0"
													step="1"
													v-model="row.qty"
													@input="syncTotals"
													placeholder="0"
												/>
											</td>
											<td>
												<input
													class="form-control form-control-sm"
													type="number"
													min="0"
													step="0.01"
													v-model="row.amount"
													@input="syncTotals"
													placeholder="0.00"
												/>
											</td>
											<td>
												<button type="button" class="btn btn-sm btn-danger" @click="removeMaterialRow(idx)">X</button>
											</td>
										</tr>
									</tbody>
									<tbody v-else>
										<tr>
											<td colspan="4" class="text-center text-muted py-3">Add at least one material</td>
										</tr>
									</tbody>
								</table>
							</div>

							<div v-if="errors.raw_material_ids" class="text-danger small">{{ errors.raw_material_ids }}</div>
							<div v-if="errors.raw_material_qty" class="text-danger small">{{ errors.raw_material_qty }}</div>
							<div v-if="errors.raw_material_price" class="text-danger small">{{ errors.raw_material_price }}</div>
						</div>

						<!-- Other product types -->
						<div v-else-if="form.product_type === '6'" class="mt-4">
							<div class="d-flex justify-content-between align-items-center border-bottom pb-2">
								<h6 class="mb-0">Products</h6>
								<button type="button" class="btn btn-sm btn-primary" @click="addProductRow">Add product</button>
							</div>

							<div class="table-responsive mt-3 purchase-table-responsive">
								<table class="table table-sm table-bordered align-middle">
									<thead class="table-light">
										<tr align="center">
											<th style="min-width: 280px">Product name</th>
											<th style="min-width: 120px">Quantity</th>
											<th style="min-width: 160px">Price (₹)</th>
											<th style="width: 80px">Remove</th>
										</tr>
									</thead>
									<tbody v-if="productRows.length">
										<tr v-for="(row, idx) in productRows" :key="'pr-' + idx" align="center">
											<td class="text-start">
												<input
													type="text"
													class="form-control form-control-sm"
													v-model="row.product_name"
													placeholder="Enter product name"
												/>
											</td>
											<td>
												<input
													class="form-control form-control-sm"
													type="number"
													min="0"
													step="1"
													v-model="row.qty"
													placeholder="0"
												/>
											</td>
											<td>
												<input
													class="form-control form-control-sm"
													type="number"
													min="0"
													step="0.01"
													v-model="row.price"
													@input="syncTotals"
													placeholder="0.00"
												/>
											</td>
											<td>
												<button type="button" class="btn btn-sm btn-danger" @click="removeProductRow(idx)">X</button>
											</td>
										</tr>
									</tbody>
									<tbody v-else>
										<tr>
											<td colspan="4" class="text-center text-muted py-3">Add at least one product</td>
										</tr>
									</tbody>
								</table>
							</div>

							<div v-if="errors.product_name" class="text-danger small">{{ errors.product_name }}</div>
							<div v-if="errors.product_qty" class="text-danger small">{{ errors.product_qty }}</div>
							<div v-if="errors.product_price" class="text-danger small">{{ errors.product_price }}</div>
						</div>

						<div v-else-if="form.product_type === '7'" class="mt-4">
							<h6 class="border-bottom pb-2">Board details</h6>
							<div class="row">
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Count<span class="text-danger">*</span></label>
									<input
										class="form-control"
										type="number"
										min="1"
										step="1"
										v-model="form.count"
										placeholder="0"
									/>
									<div v-if="errors.count" class="text-danger small mt-1">{{ errors.count }}</div>
								</div>
							</div>
						</div>

						<div v-else-if="form.product_type" class="mt-4">
							<h6 class="border-bottom pb-2">{{ productTypeLabel(form.product_type) }} details</h6>
							<div class="row">
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Quantity<span class="text-danger">*</span></label>
									<input
										class="form-control"
										type="number"
										min="0"
										step="1"
										v-model="form.product_qty"
										placeholder="0"
									/>
									<div v-if="errors.product_qty" class="text-danger small mt-1">{{ errors.product_qty }}</div>
								</div>
							</div>
						</div>

						<!-- Amount block -->
						<div v-if="form.product_type" class="mt-4">
							<h6 class="border-bottom pb-2">Amount</h6>
							<div class="row">
								<div class="col-md-3 form-group mt-2">
									<label class="control-label">Amount (₹)<span class="text-danger">*</span></label>
									<input
										class="form-control"
										type="number"
										min="0"
										step="0.01"
										v-model="form.amount"
										:readonly="form.product_type === '1' || form.product_type === '6'"
										@input="syncTotals"
										placeholder="0.00"
									/>
									<small v-if="form.product_type === '1'" class="text-muted">Auto-calculated from material rows.</small>
									<small v-else-if="form.product_type === '6'" class="text-muted">Auto-calculated from product prices.</small>
									<div v-if="errors.amount" class="text-danger small mt-1">{{ errors.amount }}</div>
								</div>

								<div class="col-md-3 form-group mt-2">
									<label class="control-label">GST amount (₹)</label>
									<input
										class="form-control"
										type="number"
										min="0"
										step="0.01"
										v-model="form.gst_amount"
										@input="syncTotals"
										placeholder="0.00"
									/>
									<small class="text-muted">Leave blank if not applicable.</small>
									<div v-if="errors.gst_amount" class="text-danger small mt-1">{{ errors.gst_amount }}</div>
								</div>

								<div class="col-md-3 form-group mt-2">
									<label class="control-label">Other amount (₹)</label>
									<input
										class="form-control"
										type="number"
										min="0"
										step="0.01"
										v-model="form.other_amounts"
										@input="syncTotals"
										placeholder="0.00"
									/>
									<div v-if="errors.other_amounts" class="text-danger small mt-1">{{ errors.other_amounts }}</div>
								</div>

								<div class="col-md-3 form-group mt-2">
									<label class="control-label">Round off amount (₹)</label>
									<input
										class="form-control"
										type="number"
										step="0.01"
										v-model="form.round_off_amount"
										@input="syncTotals"
										placeholder="0.00"
									/>
									<div v-if="errors.round_off_amount" class="text-danger small mt-1">{{ errors.round_off_amount }}</div>
								</div>

								<div class="col-md-3 form-group mt-2">
									<label class="control-label">Total amount (₹)<span class="text-danger">*</span></label>
									<input
										class="form-control"
										type="number"
										min="0"
										step="0.01"
										v-model="form.total_amount"
										readonly
										placeholder="0.00"
									/>
									<small class="text-muted">Auto-calculated as amount + GST + other + round off.</small>
									<div v-if="errors.total_amount" class="text-danger small mt-1">{{ errors.total_amount }}</div>
								</div>
							</div>

							<div class="row">
								<div class="col-md-6 form-group mt-2">
									<label class="control-label">Other amount remarks</label>
									<input
										class="form-control"
										type="text"
										v-model="form.other_amounts_remarks"
										placeholder="Optional remarks for other amount"
									/>
									<div v-if="errors.other_amounts_remarks" class="text-danger small mt-1">{{ errors.other_amounts_remarks }}</div>
								</div>

								<div class="col-md-3 form-group mt-2">
									<label class="control-label">Paid Amount (₹)<span class="text-danger">*</span></label>
									<input
										class="form-control"
										type="number"
										min="0"
										step="0.01"
										v-model="form.paid_amount"
										placeholder="0.00"
									/>
									<div v-if="errors.paid_amount" class="text-danger small mt-1">{{ errors.paid_amount }}</div>
									<div v-if="form.paid_amount > form.total_amount && form.total_amount !== ''" class="text-danger small mt-1">Paid amount cannot be greater than total amount</div>
								</div>
							</div>
						</div>

						<div class="row">
							<div class="col-md-12 form-group mt-3">
								<label class="control-label">Remarks</label>
								<textarea class="form-control" rows="2" v-model="form.remarks" placeholder="Optional remarks"></textarea>
								<div v-if="errors.remarks" class="text-danger small mt-1">{{ errors.remarks }}</div>
							</div>
						</div>

						<div class="row mt-4">
							<div class="col-12">
								<button type="submit" class="btn btn-primary" :disabled="submitting">
									<span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
									{{ submitting ? "Saving..." : "Save purchase" }}
								</button>
								<button type="button" class="btn btn-light ms-2" :disabled="submitting" @click="resetAll">Reset</button>
							</div>
						</div>

						<!-- Material dropdown portal -->
						<div
							v-if="materialDropdown.open && materialRows[materialDropdown.idx]"
							ref="materialDropdownEl"
							class="list-group"
							:style="materialDropdownStyle"
							@mousedown.prevent
						>
							<button
								v-for="m in filteredMaterials(materialRows[materialDropdown.idx].material_search)"
								:key="'mp-' + m.id"
								type="button"
								class="list-group-item list-group-item-action"
								@mousedown.prevent
								@click="selectMaterial(materialDropdown.idx, m); closeMaterialDropdown()"
							>
								<div class="fw-bold">{{ m.raw_material_name }}</div>
								<small class="text-muted">{{ m.description }}</small>
							</button>
							<div v-if="!filteredMaterials(materialRows[materialDropdown.idx].material_search).length" class="list-group-item text-muted">
								No matches
							</div>
						</div>

						<!-- Supplier dropdown portal -->
						<div
							v-if="supplierDropdown.open"
							ref="supplierDropdownEl"
							class="list-group"
							:style="supplierDropdownStyle"
							@mousedown.prevent
						>
							<button
								v-for="s in filteredSuppliers(supplierSearch)"
								:key="'sp-' + s.id"
								type="button"
								class="list-group-item list-group-item-action"
								@mousedown.prevent
								@click="selectSupplier(s); closeSupplierDropdown()"
							>
								<div class="d-flex justify-content-between">
									<span>{{ s.name }}</span>
									<small class="text-muted">{{ s.mobile || "—" }}</small>
								</div>
							</button>
							<div v-if="!filteredSuppliers(supplierSearch).length" class="list-group-item text-muted">No matches</div>
						</div>

						<!-- Vendor dropdown portal -->
						<div
							v-if="vendorDropdown.open"
							ref="vendorDropdownEl"
							class="list-group"
							:style="vendorDropdownStyle"
							@mousedown.prevent
						>
							<button
								v-for="v in filteredVendors(vendorSearch)"
								:key="'vp-' + v.id"
								type="button"
								class="list-group-item list-group-item-action"
								@mousedown.prevent
								@click="selectVendor(v); closeVendorDropdown()"
							>
								<div class="d-flex justify-content-between">
									<span>{{ v.name }}</span>
									<small class="text-muted">{{ v.mobile || "—" }}</small>
								</div>
							</button>
							<div v-if="!filteredVendors(vendorSearch).length" class="list-group-item text-muted">No matches</div>
						</div>
					</form>
				</div>
			</div>

			<div class="card mt-4">
				<div class="card-body">
					<h5 class="card-title mb-3">Purchase records</h5>

					<div class="col-md-12 project-list filter-sort">
						<div class="row align-items-end">
							<div class="col-md-6 d-flex mt-5">
								<label class="d-flex listing-length ms-1 align-items-center">
									<span class="me-2">Show</span>
									<select
										class="form-select"
										style="width: auto; min-width: 4.5rem"
										v-model="purchaseList.listing_length"
										@change="fetchPurchaseList()"
									>
										<option value="5">5</option>
										<option value="10">10</option>
										<option value="20">20</option>
										<option value="50">50</option>
									</select>
									<span class="ms-2">entries</span>
								</label>
							</div>
							<div class="col-md-2 mt-2">
								<label class="form-label small mb-1">Bank</label>
								<select class="form-select" v-model="purchaseList.bank_account_id" @change="fetchPurchaseList()">
									<option value="">All banks</option>
									<option v-for="b in bankAccounts" :key="'pl-' + b.id" :value="String(b.id)">
										{{ b.bank_name }} — {{ b.account_number }}
									</option>
								</select>
							</div>
							<div class="col-md-2 mt-2">
								<label class="form-label small mb-1">Date</label>
								<input class="form-control" type="date" v-model="purchaseList.date" @change="fetchPurchaseList()" />
							</div>
							<div class="col-md-2 mt-2">
								<label class="form-label small mb-1">Search</label>
								<input
									class="form-control"
									type="text"
									placeholder="Keyword…"
									v-model="purchaseList.keyword"
									@input="onPurchaseListKeywordInput"
								/>
							</div>
						</div>
					</div>

					<div class="table-responsive product-table mt-3">
						<table class="display table table-sm">
							<thead>
								<tr align="center">
									<th>Sl.No</th>
									<th>Transaction amount</th>
									<th>Payment type & Status</th>
									<th>Transaction type</th>
									<th>Bank</th>
									<th>Supplier / Vendor</th>
									<th>Invoice</th>
									<th>Remarks</th>
									<th>Added by</th>
									<th>Payment ID</th>
									<th>Recorded on</th>
									<th>Action</th>
								</tr>
							</thead>
							<tbody v-if="purchaseList.loading">
								<tr>
									<td colspan="12" align="center">
										<span class="spinner-border spinner-border-sm me-2"></span>
										Loading…
									</td>
								</tr>
							</tbody>
							<tbody v-else-if="purchaseList.rows.length">
								<tr align="center" v-for="(row, index) in purchaseList.rows" :key="'pr-' + row.id">
									<td>{{ purchaseList.from != null ? purchaseList.from + index : index + 1 }}</td>
									<td class="text-start small">
										<div><span class="text-muted fw-bold">Amount:</span> ₹{{ formatMoney(row.amount) }}</div>
										<div><span class="text-muted fw-bold">GST:</span> ₹{{ formatMoney(row.gst) }}</div>
										<div v-if="Number(row.other_amounts) > 0"><span class="text-muted fw-bold">Other:</span> ₹{{ formatMoney(row.other_amounts) }}</div>
										<div v-if="Number(row.round_off_amount) != 0"><span class="text-muted fw-bold">Round off:</span> ₹{{ formatMoney(row.round_off_amount) }}</div>
										<div><span class="text-muted fw-bold">Total:</span> ₹{{ formatMoney(row.total) }}</div>
										<div><span class="text-muted fw-bold">Paid:</span> ₹{{ formatMoney(row.paid_amount) }}</div>
										<div><span class="text-muted fw-bold">Balance:</span> ₹{{ formatMoney(row.total - row.paid_amount) }}</div>
									</td>
									<td>
										<span :class="purchasePaymentTypeBadgeClass(row.payment_type)">{{ purchasePaymentTypeLabel(row.payment_type) }}</span><br>
										<span class="badge rounded-pill" :class="purchasePaymentStatusClass(row.payment_status)">
											{{ purchasePaymentStatusLabel(row.payment_status) }}
										</span>
									</td>
									<td>
										<span class="badge rounded-pill bg-secondary">{{ purchaseTransactionTypeLabel(row.transaction_type) }}</span>
									</td>
									<td class="text-start small">{{ row.from_account_bank_name || "—" }}</td>
									<td class="text-start small">
										<template v-if="purchasePartySummary(row)">
											<div>{{ purchasePartySummary(row).name }}</div>
											<div class="text-muted">{{ purchasePartySummary(row).mobile }}</div>
										</template>
										<span v-else>—</span>
									</td>
									<td class="text-start small">
										<div>{{ row.invoice_number || "—" }}</div>
										<div class="text-muted">{{ row.invoice_date ? $filters.formatDate(row.invoice_date) : "—" }}</div>
									</td>
									<td class="text-start small">{{ row.remarks || "—" }}
										<br> <span class="text-muted fw-bold" v-if="row.other_amounts_remarks">Other Amount Details :- </span> {{ row.other_amounts_remarks }}
									</td>
									<td class="text-start small">{{ row.added_by_name || "—" }}</td>
									<td class="text-start small">{{ row.payment_id || "—" }}</td>
									<td class="text-start small">{{ row.converted_created_at || "—" }}</td>
									<td>
										<div class="d-flex flex-column align-items-center gap-1">
											<button type="button" class="btn btn-sm btn-outline-primary" @click="openPurchaseItemsModal(row)">
												Line Items
												<span v-if="(row.purchase_items || []).length" class="badge bg-primary ms-1">{{
													(row.purchase_items || []).length
												}}</span>
											</button>
											<button type="button" class="btn btn-sm btn-outline-secondary" @click="openPurchasePaymentLogsModal(row)">
												Payment Logs
											</button>
											<button
												v-if="Number(row.payment_status) !== 1"
												type="button"
												class="btn btn-sm btn-outline-success"
												@click="openPurchaseRePaymentModal(row)"
											>
												Update Payment
											</button>
										</div>
									</td>
								</tr>
							</tbody>
							<tbody v-else>
								<tr>
									<td colspan="12" align="center">No records found</td>
								</tr>
							</tbody>
						</table>
					</div>

					<caption v-if="purchaseList.rows.length" class="float-start mt-2 d-block">
						Showing {{ purchaseList.from }} to {{ purchaseList.to }} of {{ purchaseList.total }} entries
					</caption>
					<nav v-if="purchaseList.rows.length && purchaseList.pagination.length" aria-label="Purchase pagination" class="mt-2 float-end">
						<ul class="pagination mb-0">
							<li
								class="page-item"
								v-bind:class="[{ disabled: !page.url }, { active: page.active }]"
								v-for="(page, index) in purchaseList.pagination"
								:key="'pp-' + index + '-' + page.label"
							>
								<a class="page-link" href="#" @click.prevent="onPurchaseListPageClick(page)" tabindex="-1">
									<span v-if="index === 0">Prev</span>
									<span v-else-if="index === purchaseList.pagination.length - 1">Next</span>
									<span v-else>{{ page.label }}</span>
								</a>
							</li>
						</ul>
					</nav>
				</div>
			</div>
		</div>

		<Teleport to="body">
			<div
				v-if="purchaseItemsModal.open && purchaseItemsModal.row"
				class="modal fade show d-block"
				tabindex="-1"
				role="dialog"
				aria-modal="true"
				style="background-color: rgba(0, 0, 0, 0.5)"
				@click.self="closePurchaseItemsModal"
			>
				<div class="modal-dialog modal-xl modal-dialog-scrollable" @click.stop>
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">Purchase line items</h5>
							<button type="button" class="btn-close" aria-label="Close" @click="closePurchaseItemsModal"></button>
						</div>
						<div class="modal-body">
							<div class="small text-muted mb-3">
								<div><strong>Transaction:</strong> {{ purchaseTransactionTypeLabel(purchaseItemsModal.row.transaction_type) }}</div>
								<div><strong>Bank:</strong> {{ purchaseItemsModal.row.from_account_bank_name || "—" }}</div>
								<div><strong>Total:</strong> ₹{{ formatMoney(purchaseItemsModal.row.total) }}</div>
								<div><strong>Invoice:</strong> {{ purchaseItemsModal.row.invoice_number || "—" }}</div>
								<div><strong>Invoice date:</strong> {{ purchaseItemsModal.row.invoice_date ? $filters.formatDate(purchaseItemsModal.row.invoice_date) : "—" }}</div>
								<div><strong>Payment ID:</strong> {{ purchaseItemsModal.row.payment_id || "—" }}</div>
								<div><strong>Remarks:</strong> {{ purchaseItemsModal.row.remarks || "—" }}</div>
							</div>
							<p v-if="!(purchaseItemsModal.row.purchase_items || []).length" class="text-muted mb-0">No line items on this record.</p>
							<div v-else class="table-responsive">
								<table class="table table-sm table-bordered align-middle">
									<thead class="table-light">
										<tr align="center">
											<th>Product type</th>
											<th>Item</th>
											<th>Qty / stock</th>
											<th>Amount (₹)</th>
											<th>Supplier / Vendor</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="it in purchaseItemsModal.row.purchase_items || []" :key="'pi-' + it.id" align="center">
											<td>{{ productTypeLabel(it.product_type) }}</td>
											<td class="text-start small">{{ purchaseItemLineLabel(it) }}</td>
											<td>{{ it.stock != null && it.stock !== "" ? it.stock : "—" }}</td>
											<td>₹{{ formatMoney(it.amount) }}</td>
											<td class="text-start small">
												<template v-if="it.vendor?.name">
													<div>{{ it.vendor.name }}</div>
													<div class="text-muted">{{ it.vendor.mobile || "—" }}</div>
												</template>
												<template v-else>
													<div>{{ it.supplier?.name || "—" }}</div>
													<div class="text-muted">{{ it.supplier?.mobile || "—" }}</div>
												</template>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-light" @click="closePurchaseItemsModal">Close</button>
						</div>
					</div>
				</div>
			</div>

			<div
				v-if="purchasePaymentLogsModal.open && purchasePaymentLogsModal.row"
				class="modal fade show d-block"
				tabindex="-1"
				role="dialog"
				aria-modal="true"
				style="background-color: rgba(0, 0, 0, 0.5)"
				@click.self="closePurchasePaymentLogsModal"
			>
				<div class="modal-dialog modal-xl modal-dialog-scrollable" @click.stop>
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">Payment log</h5>
							<button type="button" class="btn-close" aria-label="Close" @click="closePurchasePaymentLogsModal"></button>
						</div>
						<div class="modal-body">
							<div class="small text-muted mb-3">
								<div><strong>Transaction:</strong> {{ purchaseTransactionTypeLabel(purchasePaymentLogsModal.row.transaction_type) }}</div>
								<div><strong>Bank:</strong> {{ purchasePaymentLogsModal.row.from_account_bank_name || "—" }}</div>
								<div><strong>Total:</strong> ₹{{ formatMoney(purchasePaymentLogsModal.row.total) }}</div>
								<div><strong>Paid:</strong> ₹{{ formatMoney(purchasePaymentLogsModal.row.paid_amount) }}</div>
								<div><strong>Balance:</strong> ₹{{ formatMoney(purchaseRePaymentPendingAmount(purchasePaymentLogsModal.row)) }}</div>
								<div><strong>Invoice:</strong> {{ purchasePaymentLogsModal.row.invoice_number || "—" }}</div>
								<div><strong>Payment status:</strong>
									<span class="badge rounded-pill ms-1" :class="purchasePaymentStatusClass(purchasePaymentLogsModal.row.payment_status)">
										{{ purchasePaymentStatusLabel(purchasePaymentLogsModal.row.payment_status) }}
									</span>
								</div>
							</div>

							<div v-if="purchasePaymentLogsModal.loading" class="text-center py-4">
								<span class="spinner-border spinner-border-sm me-2"></span>
								Loading payment logs…
							</div>
							<p v-else-if="purchasePaymentLogsModal.error" class="text-danger mb-0">{{ purchasePaymentLogsModal.error }}</p>
							<p v-else-if="!purchasePaymentLogsModal.logs.length" class="text-muted mb-0">No payment logs for this record.</p>
							<div v-else class="table-responsive">
								<table class="table table-sm table-bordered align-middle">
									<thead class="table-light">
										<tr align="center">
											<th>#</th>
											<th>Transaction amount (₹)</th>
											<th>Status</th>
											<th>Pending (₹)</th>
											<th>Credit (₹)</th>
											<th>Total (₹)</th>
											<th>Bank</th>
											<th>Payment ID</th>
											<th>Payment date</th>
											<th>Recorded on</th>
											<th>Action</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="(log, idx) in purchasePaymentLogsModal.logs" :key="'pl-' + log.id" align="center">
											<td>{{ idx + 1 }}</td>
											<td>₹{{ formatMoney(log.transaction_amount) }}</td>
											<td>
												<span class="badge rounded-pill" :class="purchasePaymentStatusClass(log.status)">
													{{ purchasePaymentStatusLabel(log.status) }}
												</span>
											</td>
											<td>₹{{ formatMoney(log.pending_amount) }}</td>
											<td>₹{{ formatMoney(log.credit_amount) }}</td>
											<td>₹{{ formatMoney(log.total_amount) }}</td>
											<td class="text-start small">{{ log.bank_name || "—" }}</td>
											<td class="text-start small">{{ log.payment_id || "—" }}</td>
											<td>{{ log.payment_date ? $filters.formatDate(log.payment_date) : "—" }}</td>
											<td class="text-start small">{{ log.created_at ? $filters.formatDate(log.created_at) : "—" }}</td>
											<td>
												<button
													v-if="Number(log.status) !== 2 && $canRevoke()"
													type="button"
													class="btn btn-sm btn-outline-danger"
													@click="openRevokePurchaseLogModal(log)"
												>
													Revoke
												</button>
												<span v-else>—</span>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-light" @click="closePurchasePaymentLogsModal">Close</button>
						</div>
					</div>
				</div>
			</div>

			<div
				v-if="purchaseRePaymentModal.open && purchaseRePaymentModal.row"
				class="modal fade show d-block"
				tabindex="-1"
				role="dialog"
				aria-modal="true"
				style="background-color: rgba(0, 0, 0, 0.5)"
				@click.self="closePurchaseRePaymentModal"
			>
				<div class="modal-dialog modal-dialog-scrollable" @click.stop>
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">Update payment</h5>
							<button type="button" class="btn-close" aria-label="Close" @click="closePurchaseRePaymentModal"></button>
						</div>
						<form @submit.prevent="submitPurchaseRePayment">
							<div class="modal-body">
								<div class="card border-light mb-3">
									<div class="card-body py-2">
										<div class="row text-center small">
											<div class="col-4">
												<div class="text-muted">Total amount</div>
												<div class="fw-bold">₹{{ formatMoney(purchaseRePaymentModal.row.total) }}</div>
											</div>
											<div class="col-4">
												<div class="text-muted">Paid so far</div>
												<div class="fw-bold">₹{{ formatMoney(purchaseRePaymentModal.row.paid_amount) }}</div>
											</div>
											<div class="col-4">
												<div class="text-muted">Pending balance</div>
												<div class="fw-bold text-warning">₹{{ formatMoney(purchaseRePaymentPendingAmount(purchaseRePaymentModal.row)) }}</div>
											</div>
										</div>
									</div>
								</div>

								<div class="form-group mb-2">
									<label class="control-label">Bank account<span class="text-danger">*</span></label>
									<select class="form-select" v-model="purchaseRePaymentModal.form.bank_account_id">
										<option value="">— Select bank —</option>
										<option v-for="b in bankAccounts" :key="'rep-' + b.id" :value="String(b.id)">
											{{ b.bank_name }} — {{ b.account_number }}
										</option>
									</select>
									<div v-if="purchaseRePaymentModal.errors.bank_account_id" class="text-danger small mt-1">
										{{ purchaseRePaymentModal.errors.bank_account_id }}
									</div>
								</div>

								<div class="form-group mb-2">
									<label class="control-label">Paid amount (₹)<span class="text-danger">*</span></label>
									<input
										class="form-control"
										type="number"
										min="0"
										step="0.01"
										:max="purchaseRePaymentPendingAmount(purchaseRePaymentModal.row)"
										v-model="purchaseRePaymentModal.form.paid_amount"
										placeholder="0.00"
									/>
									<small class="text-muted">Cannot exceed pending balance (₹{{ formatMoney(purchaseRePaymentPendingAmount(purchaseRePaymentModal.row)) }}).</small>
									<div v-if="purchaseRePaymentModal.errors.paid_amount" class="text-danger small mt-1">
										{{ purchaseRePaymentModal.errors.paid_amount }}
									</div>
								</div>

								<div class="form-group mb-2">
									<label class="control-label">Payment date<span class="text-danger">*</span></label>
									<input class="form-control" type="date" v-model="purchaseRePaymentModal.form.payment_date" />
									<div v-if="purchaseRePaymentModal.errors.payment_date" class="text-danger small mt-1">
										{{ purchaseRePaymentModal.errors.payment_date }}
									</div>
								</div>

								<div class="form-group mb-0">
									<label class="control-label">Payment / transaction ID</label>
									<input
										class="form-control"
										type="text"
										v-model="purchaseRePaymentModal.form.payment_id"
										placeholder="UTR / reference"
									/>
									<div v-if="purchaseRePaymentModal.errors.payment_id" class="text-danger small mt-1">
										{{ purchaseRePaymentModal.errors.payment_id }}
									</div>
								</div>
							</div>
							<div class="modal-footer">
								<button type="button" class="btn btn-light" @click="closePurchaseRePaymentModal" :disabled="purchaseRePaymentModal.submitting">
									Cancel
								</button>
								<button type="submit" class="btn btn-primary" :disabled="purchaseRePaymentModal.submitting">
									<span v-if="purchaseRePaymentModal.submitting" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
									{{ purchaseRePaymentModal.submitting ? "Saving..." : "Submit payment" }}
								</button>
							</div>
						</form>
					</div>
				</div>
			</div>

			<div
				v-if="revokePurchaseLogModal.open && revokePurchaseLogModal.log"
				class="modal fade show d-block"
				tabindex="-1"
				role="dialog"
				aria-modal="true"
				style="background-color: rgba(0, 0, 0, 0.5); z-index: 1060"
				@click.self="closeRevokePurchaseLogModal"
			>
				<div class="modal-dialog modal-dialog-scrollable" style="z-index: 1061" @click.stop>
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">Revoke payment log</h5>
							<button type="button" class="btn-close" aria-label="Close" @click="closeRevokePurchaseLogModal"></button>
						</div>
						<div class="modal-body">
							<p class="mb-3">Are you sure you want to revoke this payment log?</p>
							<div class="small border rounded p-3 bg-light text-dark">
								<div class="mb-1"><strong>Payment log ID:</strong> #{{ revokePurchaseLogModal.log.id }}</div>
								<div class="mb-1"><strong>Transaction amount:</strong> ₹{{ formatMoney(revokePurchaseLogModal.log.transaction_amount) }}</div>
								<div class="mb-1"><strong>Status:</strong> {{ purchasePaymentStatusLabel(revokePurchaseLogModal.log.status) }}</div>
								<div class="mb-1"><strong>Pending:</strong> ₹{{ formatMoney(revokePurchaseLogModal.log.pending_amount) }}</div>
								<div class="mb-1"><strong>Credit:</strong> ₹{{ formatMoney(revokePurchaseLogModal.log.credit_amount) }}</div>
								<div class="mb-1"><strong>Total:</strong> ₹{{ formatMoney(revokePurchaseLogModal.log.total_amount) }}</div>
								<div class="mb-1"><strong>Bank:</strong> {{ revokePurchaseLogModal.log.bank_name || "—" }}</div>
								<div class="mb-1"><strong>Payment ID:</strong> {{ revokePurchaseLogModal.log.payment_id || "—" }}</div>
								<div class="mb-1"><strong>Payment date:</strong> {{ revokePurchaseLogModal.log.payment_date ? $filters.formatDate(revokePurchaseLogModal.log.payment_date) : "—" }}</div>
								<div class="mb-0"><strong>Recorded on:</strong> {{ revokePurchaseLogModal.log.created_at ? $filters.formatDate(revokePurchaseLogModal.log.created_at) : "—" }}</div>
							</div>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-danger" :disabled="revokePurchaseLogModal.submitting" @click="confirmRevokePurchase">
								<span v-if="revokePurchaseLogModal.submitting" class="spinner-border spinner-border-sm me-2"></span>
								Yes, revoke
							</button>
							<button type="button" class="btn btn-light" :disabled="revokePurchaseLogModal.submitting" @click="closeRevokePurchaseLogModal">No</button>
						</div>
					</div>
				</div>
			</div>
		</Teleport>
	</div>
</template>

<script>
export default {
	name: "Purchase",
	data() {
		return {
			token: "",
			submitting: false,
			errors: {},

			bankAccounts: [],
			rawMaterials: [],
			suppliers: [],
			vendors: [],

			materialRows: [],
			productRows: [],

			supplierSearch: "",
			vendorSearch: "",

			materialDropdown: {
				open: false,
				idx: -1,
				top: 0,
				left: 0,
				width: 0,
			},
			closeMaterialDropdownTimer: null,

			supplierDropdown: {
				open: false,
				top: 0,
				left: 0,
				width: 0,
			},
			closeSupplierDropdownTimer: null,

			vendorDropdown: {
				open: false,
				top: 0,
				left: 0,
				width: 0,
			},
			closeVendorDropdownTimer: null,

			form: {
				product_type: "",
				bank_account_id: "",
				supplier_id: "",
				vendor_id: "",
				count: "",
				amount: "",
				gst_amount: "",
				other_amounts: 0,
				other_amounts_remarks: "",
				round_off_amount: 0,
				total_amount: "",
				product_qty: "",
				product_name: "",
				payment_id: "",
				payment_date: "",
				invoice_number: "",
				invoice_date: "",
				remarks: "",
				user_name: "",
				user_mobile: "",
				paid_amount: 0,
			},

			purchaseList: {
				listing_length: "10",
				keyword: "",
				date: "",
				bank_account_id: "",
				rows: [],
				pagination: [],
				from: 0,
				to: 0,
				total: 0,
				loading: false,
				active_page_url: "",
			},
			purchaseListKeywordDebounceTimer: null,

			purchaseItemsModal: {
				open: false,
				row: null,
			},

			purchasePaymentLogsModal: {
				open: false,
				row: null,
				logs: [],
				loading: false,
				error: "",
			},

			revokePurchaseLogModal: {
				open: false,
				log: null,
				submitting: false,
			},

			purchaseRePaymentModal: {
				open: false,
				row: null,
				submitting: false,
				form: {
					bank_account_id: "",
					paid_amount: 0,
					payment_date: "",
					payment_id: "",
				},
				errors: {},
			},
		};
	},
	computed: {
		materialDropdownStyle() {
			return {
				position: "fixed",
				top: `${this.materialDropdown.top}px`,
				left: `${this.materialDropdown.left}px`,
				width: `${this.materialDropdown.width}px`,
				zIndex: 2000,
				maxHeight: "260px",
				overflow: "auto",
			};
		},
		supplierDropdownStyle() {
			return {
				position: "fixed",
				top: `${this.supplierDropdown.top}px`,
				left: `${this.supplierDropdown.left}px`,
				width: `${this.supplierDropdown.width}px`,
				zIndex: 2000,
				maxHeight: "220px",
				overflow: "auto",
			};
		},
		vendorDropdownStyle() {
			return {
				position: "fixed",
				top: `${this.vendorDropdown.top}px`,
				left: `${this.vendorDropdown.left}px`,
				width: `${this.vendorDropdown.width}px`,
				zIndex: 2000,
				maxHeight: "220px",
				overflow: "auto",
			};
		},
	},
	created() {
		this.init();
	},
	mounted() {
		window.addEventListener("scroll", this.handleWindowScroll, true);
		window.addEventListener("resize", this.closeAllDropdowns);
	},
	beforeUnmount() {
		window.removeEventListener("scroll", this.handleWindowScroll, true);
		window.removeEventListener("resize", this.closeAllDropdowns);
		if (this.closeMaterialDropdownTimer) clearTimeout(this.closeMaterialDropdownTimer);
		if (this.closeSupplierDropdownTimer) clearTimeout(this.closeSupplierDropdownTimer);
		if (this.closeVendorDropdownTimer) clearTimeout(this.closeVendorDropdownTimer);
		if (this.purchaseListKeywordDebounceTimer) {
			clearTimeout(this.purchaseListKeywordDebounceTimer);
			this.purchaseListKeywordDebounceTimer = null;
		}
	},
	methods: {
		getToken() {
			if (localStorage.getItem("airotrack_staff")) {
				const airotrack_staff = JSON.parse(localStorage.getItem("airotrack_staff"));
				if (airotrack_staff.token) {
					this.token = airotrack_staff.token;
					return;
				}
			}
			this.$swal("", "Session expired, please login again", "error");
			this.$router.push({ name: "login" });
		},

		async init() {
			this.getToken();
			await Promise.all([this.fetchBankAccounts(), this.fetchRawMaterials(), this.fetchSuppliers(), this.fetchVendors()]);
			this.fetchPurchaseList();
		},

		buildPurchaseQueryString() {
			const kw = String(this.purchaseList.keyword || "").trim();
			const date = String(this.purchaseList.date || "").trim();
			const bankId = String(this.purchaseList.bank_account_id || "").trim();
			return [
				`limit=${encodeURIComponent(this.purchaseList.listing_length)}`,
				`keyword=${encodeURIComponent(kw)}`,
				`date=${encodeURIComponent(date)}`,
				`bank_account_id=${encodeURIComponent(bankId)}`,
			].join("&");
		},

		purchaseListUrl() {
			return `/staffs/getPurchase?${this.buildPurchaseQueryString()}`;
		},

		onPurchaseListKeywordInput() {
			if (this.purchaseListKeywordDebounceTimer) {
				clearTimeout(this.purchaseListKeywordDebounceTimer);
			}
			this.purchaseListKeywordDebounceTimer = setTimeout(() => {
				this.purchaseListKeywordDebounceTimer = null;
				this.fetchPurchaseList();
			}, 400);
		},

		fetchPurchaseList(pageUrl) {
			this.getToken();
			this.purchaseList.loading = true;
			const url = pageUrl || this.purchaseListUrl();
			this.purchaseList.active_page_url = url;
			this.$axios
				.get(url, { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const payload = res.data?.data || {};
					// API nests the paginator under `expense` (same shape as getExpense); `purchase` as fallback.
					const paginator = payload.expense || payload.purchase;
					if (!paginator) {
						this.purchaseList.rows = [];
						this.purchaseList.pagination = [];
						this.purchaseList.from = 0;
						this.purchaseList.to = 0;
						this.purchaseList.total = 0;
						return;
					}
					this.purchaseList.rows = paginator.data || [];
					this.purchaseList.pagination = paginator.links || [];
					this.purchaseList.from = paginator.from ?? 0;
					this.purchaseList.to = paginator.to ?? 0;
					this.purchaseList.total = paginator.total ?? 0;
				})
				.catch((err) => {
					console.error(err);
					this.purchaseList.rows = [];
					this.purchaseList.pagination = [];
					this.purchaseList.from = 0;
					this.purchaseList.to = 0;
					this.purchaseList.total = 0;
					this.$swal("Error", err.response?.data?.message || "Failed to load purchase list", "error");
				})
				.finally(() => {
					this.purchaseList.loading = false;
				});
		},

		onPurchaseListPageClick(page) {
			if (!page.url) return;
			const base = page.url;
			const sep = base.includes("?") ? "&" : "?";
			this.fetchPurchaseList(`${base}${sep}${this.buildPurchaseQueryString()}`);
		},

		formatMoney(value) {
			const n = parseFloat(value);
			if (isNaN(n)) return "0.00";
			return n.toFixed(2);
		},

		purchasePaymentTypeLabel(v) {
			const map = { 1: "Credit", 2: "Debit", 3: "Contra" };
			return map[Number(v)] || "—";
		},

		purchasePaymentTypeBadgeClass(v) {
			const n = Number(v);
			if (n === 1) return "badge rounded-pill bg-success";
			if (n === 2) return "badge rounded-pill bg-secondary";
			if (n === 3) return "badge rounded-pill bg-info text-dark";
			return "badge rounded-pill bg-secondary";
		},

		purchaseTransactionTypeLabel(v) {
			const map = { 1: "Contra", 2: "Deposit", 3: "Purchase", 4: "Sale" };
			return map[Number(v)] || "—";
		},

		purchasePaymentStatusClass(v) {
			const n = Number(v);
			if (n === 1) return "bg-success";
			if (n === 2) return "bg-secondary";
			if (n === 3) return "bg-warning";
			if (n === 4) return "bg-info";
			return "bg-secondary";
		},

		purchasePaymentStatusLabel(v) {
			const map = { 1: "Paid", 2: "Pending", 3: "Partially Paid", 4: "Credit" };
			return map[Number(v)] || "—";
		},

		purchasePartySummary(row) {
			const items = row?.purchase_items || [];
			if (!items.length) return null;

			const vendorIds = new Set(items.map((i) => i.airo_vendor_id).filter((x) => x != null && x !== ""));
			const firstVendor = items.find((i) => i.vendor?.name);
			if (firstVendor?.vendor?.name) {
				const v = firstVendor.vendor;
				const name = vendorIds.size > 1 ? `${v.name} (+${vendorIds.size - 1} vendors)` : v.name;
				return { name, mobile: v.mobile || "—" };
			}

			const supplierIds = new Set(items.map((i) => i.airo_supplier_id).filter((x) => x != null && x !== ""));
			const firstSupplier = items.find((i) => i.supplier?.name) || items[0];
			const s = firstSupplier?.supplier;
			if (!s || !s.name) return null;
			const name = supplierIds.size > 1 ? `${s.name} (+${supplierIds.size - 1} suppliers)` : s.name;
			return { name, mobile: s.mobile || "—" };
		},

		purchaseItemLineLabel(it) {
			if (Number(it.product_type) === 7) return "Board";
			if (it.raw_material?.raw_material_name) return it.raw_material.raw_material_name;
			if (it.product_name) return it.product_name;
			return "—";
		},

		openPurchaseItemsModal(row) {
			this.purchaseItemsModal.row = row;
			this.purchaseItemsModal.open = true;
		},

		closePurchaseItemsModal() {
			this.purchaseItemsModal.open = false;
			this.purchaseItemsModal.row = null;
		},

		openPurchasePaymentLogsModal(row) {
			this.purchasePaymentLogsModal.row = row;
			this.purchasePaymentLogsModal.open = true;
			this.purchasePaymentLogsModal.logs = [];
			this.purchasePaymentLogsModal.error = "";
			this.fetchPurchasePaymentLogs(row.id);
		},

		closePurchasePaymentLogsModal() {
			this.closeRevokePurchaseLogModal();
			this.purchasePaymentLogsModal.open = false;
			this.purchasePaymentLogsModal.row = null;
			this.purchasePaymentLogsModal.logs = [];
			this.purchasePaymentLogsModal.loading = false;
			this.purchasePaymentLogsModal.error = "";
		},

		fetchPurchasePaymentLogs(transactionId) {
			this.getToken();
			this.purchasePaymentLogsModal.loading = true;
			this.purchasePaymentLogsModal.error = "";
			this.$axios
				.get(`/staffs/purchasePaymentLogs?transaction_id=${encodeURIComponent(transactionId)}`, {
					headers: { Authorization: "Bearer " + this.token },
				})
				.then((res) => {
					const data = res.data?.data || res.data || {};
					const logs = data.airo_payment_logs;
					this.purchasePaymentLogsModal.logs = Array.isArray(logs) ? logs : [];
				})
				.catch((err) => {
					this.purchasePaymentLogsModal.logs = [];
					this.purchasePaymentLogsModal.error = err.response?.data?.message || "Failed to load payment logs";
				})
				.finally(() => {
					this.purchasePaymentLogsModal.loading = false;
				});
		},

		openRevokePurchaseLogModal(log) {
			if (!this.$canRevoke()) return;
			this.revokePurchaseLogModal.log = log;
			this.revokePurchaseLogModal.open = true;
			this.revokePurchaseLogModal.submitting = false;
		},

		closeRevokePurchaseLogModal() {
			this.revokePurchaseLogModal.open = false;
			this.revokePurchaseLogModal.log = null;
			this.revokePurchaseLogModal.submitting = false;
		},

		isApiSuccess(data) {
			return data?.status === true || data?.status === "true" || data?.success === true || data?.success === "true";
		},

		async confirmRevokePurchase() {
			if (!this.revokePurchaseLogModal.log?.id) return;
			this.getToken();
			this.revokePurchaseLogModal.submitting = true;
			const formData = new FormData();
			formData.append("airo_payment_log_id", this.revokePurchaseLogModal.log.id);
			const purchaseRowId = this.purchasePaymentLogsModal.row?.id;
			try {
				const res = await this.$axios.post("/staffs/revokePurchase", formData, {
					headers: {
						"content-type": "multipart/form-data",
						Authorization: "Bearer " + this.token,
					},
				});
				if (this.isApiSuccess(res.data)) {
					this.$swal("Success", res.data?.message || "Payment log revoked", "success");
					this.closeRevokePurchaseLogModal();
					if (purchaseRowId) {
						this.fetchPurchasePaymentLogs(purchaseRowId);
					}
					this.fetchPurchaseList(this.purchaseList.active_page_url);
				} else {
					this.$swal("Error", res.data?.message || "Failed to revoke payment log", "error");
				}
			} catch (err) {
				this.$swal("Error", err?.response?.data?.message || "Failed to revoke payment log", "error");
			} finally {
				this.revokePurchaseLogModal.submitting = false;
			}
		},

		purchaseRePaymentPendingAmount(row) {
			if (!row) return 0;
			const total = parseFloat(row.total) || 0;
			const paid = parseFloat(row.paid_amount) || 0;
			return Math.max(0, total - paid);
		},

		openPurchaseRePaymentModal(row) {
			this.purchaseRePaymentModal.row = row;
			this.purchaseRePaymentModal.submitting = false;
			this.purchaseRePaymentModal.errors = {};
			this.purchaseRePaymentModal.form = {
				bank_account_id: "",
				paid_amount: 0,
				payment_date: "",
				payment_id: "",
			};
			this.purchaseRePaymentModal.open = true;
		},

		closePurchaseRePaymentModal() {
			this.purchaseRePaymentModal.open = false;
			this.purchaseRePaymentModal.row = null;
			this.purchaseRePaymentModal.submitting = false;
			this.purchaseRePaymentModal.errors = {};
			this.purchaseRePaymentModal.form = {
				bank_account_id: "",
				paid_amount: 0,
				payment_date: "",
				payment_id: "",
			};
		},

		validatePurchaseRePayment() {
			const errors = {};
			const f = this.purchaseRePaymentModal.form;
			const row = this.purchaseRePaymentModal.row;
			const pending = this.purchaseRePaymentPendingAmount(row);

			if (!f.bank_account_id) {
				errors.bank_account_id = "Select bank account";
			}
			if (!f.payment_date) {
				errors.payment_date = "Select payment date";
			}

			const paid = parseFloat(f.paid_amount);
			if (isNaN(paid) || paid <= 0) {
				errors.paid_amount = "Enter a valid paid amount";
			} else if (paid > pending) {
				errors.paid_amount = `Paid amount cannot exceed pending balance (₹${this.formatMoney(pending)})`;
			}

			this.purchaseRePaymentModal.errors = errors;
			return Object.keys(errors).length === 0;
		},

		async submitPurchaseRePayment() {
			if (this.purchaseRePaymentModal.submitting) return;
			if (!this.validatePurchaseRePayment()) return;

			this.purchaseRePaymentModal.submitting = true;
			this.getToken();

			const f = this.purchaseRePaymentModal.form;
			const row = this.purchaseRePaymentModal.row;
			const payload = {
				airo_payment_id: row.id,
				payment_date: f.payment_date,
				payment_id: String(f.payment_id || "").trim() || null,
				paid_amount: parseFloat(f.paid_amount),
				bank_account_id: parseInt(f.bank_account_id, 10),
			};

			try {
				const res = await this.$axios.post("/staffs/purchaseRePayment", payload, {
					headers: { Authorization: "Bearer " + this.token, "Content-Type": "application/json" },
				});
				const ok = res.data?.status === true || res.data?.status === "true";
				if (ok) {
					this.$swal("Success", res.data?.message || "Payment updated", "success");
					this.closePurchaseRePaymentModal();
					this.fetchPurchaseList(this.purchaseList.active_page_url || undefined);
				} else {
					this.$swal("Error", res.data?.message || "Failed to update payment", "error");
				}
			} catch (err) {
				const msg = err?.response?.data?.message || "Something went wrong";
				const fieldErrors = err?.response?.data?.errors;
				if (fieldErrors && typeof fieldErrors === "object") {
					Object.keys(fieldErrors).forEach((k) => {
						const v = fieldErrors[k];
						this.purchaseRePaymentModal.errors[k] = Array.isArray(v) ? v[0] : v;
					});
				}
				this.$swal("Error", msg, "error");
			} finally {
				this.purchaseRePaymentModal.submitting = false;
			}
		},

		fetchBankAccounts() {
			return this.$axios
				.get("/staffs/bankAccounts", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const data = res.data.data || res.data;
					this.bankAccounts = Array.isArray(data) ? data : data?.data || [];
				})
				.catch(() => {
					this.bankAccounts = [];
				});
		},

		fetchRawMaterials() {
			return this.$axios
				.get("/staffs/getRawMaterials", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const d = res.data?.data || res.data;
					if (Array.isArray(d)) {
						this.rawMaterials = d;
					} else if (Array.isArray(d?.data)) {
						this.rawMaterials = d.data;
					} else if (Array.isArray(d?.rawMaterials)) {
						this.rawMaterials = d.rawMaterials;
					} else {
						this.rawMaterials = [];
					}
				})
				.catch(() => {
					this.rawMaterials = [];
				});
		},

		fetchSuppliers() {
			return this.$axios
				.get("/staffs/suppliersList", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const d = res.data?.data || {};
					const list = Array.isArray(d?.suppliers) ? d.suppliers : d?.suppliers?.data || [];
					this.suppliers = Array.isArray(list) ? list : [];
				})
				.catch(() => {
					this.suppliers = [];
				});
		},

		fetchVendors() {
			return this.$axios
				.get("/staffs/vendorsList", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const d = res.data?.data || res.data;
					if (Array.isArray(d)) {
						this.vendors = d;
					} else if (Array.isArray(d?.data)) {
						this.vendors = d.data;
					} else if (Array.isArray(d?.vendors)) {
						this.vendors = d.vendors;
					} else {
						this.vendors = [];
					}
				})
				.catch(() => {
					this.vendors = [];
				});
		},

		productTypeLabel(t) {
			const map = { 1: "Raw Material", 2: "GPS", 3: "SIM", 4: "Camera", 5: "Speed Governer", 6: "Other", 7: "Board" };
			return map[String(t)] || "Product";
		},

		onProductTypeChange() {
			this.errors = {};
			this.materialRows = [];
			this.productRows = [];
			this.form.amount = "";
			this.form.gst_amount = "";
			this.form.other_amounts = 0;
			this.form.other_amounts_remarks = "";
			this.form.round_off_amount = 0;
			this.form.total_amount = "";
			this.form.product_qty = "";
			this.form.product_name = "";
			this.form.count = "";
			this.clearVendor();
			if (this.form.product_type === "7") {
				this.clearSupplier();
			}
			if (this.form.product_type === "1") {
				this.addMaterialRow();
			} else if (this.form.product_type === "6") {
				this.addProductRow();
			}
		},

		addMaterialRow() {
			this.materialRows.push({ material_id: "", material_search: "", qty: "", amount: "" });
			this.syncTotals();
		},

		removeMaterialRow(idx) {
			this.materialRows.splice(idx, 1);
			this.syncTotals();
		},

		addProductRow() {
			this.productRows.push({ product_name: "", qty: "", price: "" });
			this.syncTotals();
		},

		removeProductRow(idx) {
			this.productRows.splice(idx, 1);
			this.syncTotals();
		},

		syncTotals() {
			if (this.form.product_type === "1") {
				const sum = this.materialRows.reduce((t, r) => {
					const a = parseFloat(r.amount);
					if (isNaN(a) || a < 0) return t;
					return t + a;
				}, 0);
				this.form.amount = sum ? sum.toFixed(2) : "";
			} else if (this.form.product_type === "6") {
				const sum = this.productRows.reduce((t, r) => {
					const p = parseFloat(r.price);
					if (isNaN(p) || p < 0) return t;
					return t + p;
				}, 0);
				this.form.amount = sum ? sum.toFixed(2) : "";
			}

			const a = parseFloat(this.form.amount);
			const g = parseFloat(this.form.gst_amount);
			const o = parseFloat(this.form.other_amounts);
			const r = parseFloat(this.form.round_off_amount);
			const hasA = !isNaN(a);
			const hasG = !isNaN(g);
			const hasO = !isNaN(o);
			const hasR = !isNaN(r);
			if (!hasA && !hasG && !hasO && !hasR) {
				this.form.total_amount = "";
				return;
			}
			const total = (hasA ? a : 0) + (hasG ? g : 0) + (hasO ? o : 0) + (hasR ? r : 0);
			this.form.total_amount = total.toFixed(2);
		},

		// ---- Material dropdown helpers ----
		openMaterialDropdown(idx, evt) {
			if (!this.materialRows[idx]) return;
			if (this.closeMaterialDropdownTimer) {
				clearTimeout(this.closeMaterialDropdownTimer);
				this.closeMaterialDropdownTimer = null;
			}
			const el = evt?.target;
			if (!el || !el.getBoundingClientRect) return;
			const r = el.getBoundingClientRect();
			this.materialDropdown.open = true;
			this.materialDropdown.idx = idx;
			this.materialDropdown.top = Math.round(r.bottom + 2);
			this.materialDropdown.left = Math.round(r.left);
			this.materialDropdown.width = Math.round(r.width);
		},
		scheduleCloseMaterialDropdown() {
			if (this.closeMaterialDropdownTimer) clearTimeout(this.closeMaterialDropdownTimer);
			this.closeMaterialDropdownTimer = setTimeout(() => {
				this.closeMaterialDropdown();
			}, 180);
		},
		closeMaterialDropdown() {
			this.materialDropdown.open = false;
			this.materialDropdown.idx = -1;
		},
		filteredMaterials(search) {
			const q = String(search || "").toLowerCase().trim();
			if (!q) return this.rawMaterials.slice(0, 20);
			return this.rawMaterials
				.filter((m) => {
					const name = String(m.raw_material_name || "").toLowerCase();
					const desc = String(m.description || "").toLowerCase();
					return name.includes(q) || desc.includes(q);
				})
				.slice(0, 20);
		},
		selectMaterial(idx, mat) {
			if (!this.materialRows[idx]) return;
			this.materialRows[idx].material_id = String(mat?.id || "");
			this.materialRows[idx].material_search = mat?.raw_material_name ? String(mat.raw_material_name) : "";
		},
		materialLabelById(id) {
			const m = this.rawMaterials.find((x) => String(x.id) === String(id));
			if (!m) return "#" + String(id);
			return `${m.raw_material_name}`;
		},

		// ---- Supplier dropdown helpers ----
		openSupplierDropdown(evt) {
			if (this.closeSupplierDropdownTimer) {
				clearTimeout(this.closeSupplierDropdownTimer);
				this.closeSupplierDropdownTimer = null;
			}
			const el = evt?.target;
			if (!el || !el.getBoundingClientRect) return;
			const r = el.getBoundingClientRect();
			this.supplierDropdown.open = true;
			this.supplierDropdown.top = Math.round(r.bottom + 2);
			this.supplierDropdown.left = Math.round(r.left);
			this.supplierDropdown.width = Math.round(r.width);
		},
		scheduleCloseSupplierDropdown() {
			if (this.closeSupplierDropdownTimer) clearTimeout(this.closeSupplierDropdownTimer);
			this.closeSupplierDropdownTimer = setTimeout(() => {
				this.closeSupplierDropdown();
			}, 180);
		},
		closeSupplierDropdown() {
			this.supplierDropdown.open = false;
		},
		filteredSuppliers(search) {
			const q = String(search || "").toLowerCase().trim();
			if (!q) return this.suppliers.slice(0, 20);
			return this.suppliers
				.filter((s) => {
					const name = String(s.name || "").toLowerCase();
					const mobile = String(s.mobile || "").toLowerCase();
					return name.includes(q) || mobile.includes(q);
				})
				.slice(0, 20);
		},
		selectSupplier(s) {
			this.form.supplier_id = String(s?.id || "");
			this.supplierSearch = s?.name ? String(s.name) : "";
		},
		supplierLabelById(id) {
			const s = this.suppliers.find((x) => String(x.id) === String(id));
			if (!s) return "#" + String(id);
			return `${s.name}${s.mobile ? " (" + s.mobile + ")" : ""}`;
		},
		clearSupplier() {
			this.form.supplier_id = "";
			this.supplierSearch = "";
		},

		openVendorDropdown(evt) {
			if (this.closeVendorDropdownTimer) {
				clearTimeout(this.closeVendorDropdownTimer);
				this.closeVendorDropdownTimer = null;
			}
			const el = evt?.target;
			if (!el || !el.getBoundingClientRect) return;
			const r = el.getBoundingClientRect();
			this.vendorDropdown.open = true;
			this.vendorDropdown.top = Math.round(r.bottom + 2);
			this.vendorDropdown.left = Math.round(r.left);
			this.vendorDropdown.width = Math.round(r.width);
		},
		scheduleCloseVendorDropdown() {
			if (this.closeVendorDropdownTimer) clearTimeout(this.closeVendorDropdownTimer);
			this.closeVendorDropdownTimer = setTimeout(() => {
				this.closeVendorDropdown();
			}, 180);
		},
		closeVendorDropdown() {
			this.vendorDropdown.open = false;
		},
		filteredVendors(search) {
			const q = String(search || "").toLowerCase().trim();
			if (!q) return this.vendors.slice(0, 20);
			return this.vendors
				.filter((v) => {
					const name = String(v.name || "").toLowerCase();
					const mobile = String(v.mobile || "").toLowerCase();
					return name.includes(q) || mobile.includes(q);
				})
				.slice(0, 20);
		},
		selectVendor(v) {
			this.form.vendor_id = String(v?.id || "");
			this.vendorSearch = v?.name ? String(v.name) : "";
		},
		vendorLabelById(id) {
			const v = this.vendors.find((x) => String(x.id) === String(id));
			if (!v) return "#" + String(id);
			return `${v.name}${v.mobile ? " (" + v.mobile + ")" : ""}`;
		},
		clearVendor() {
			this.form.vendor_id = "";
			this.vendorSearch = "";
		},

		closeAllDropdowns() {
			this.closeMaterialDropdown();
			this.closeSupplierDropdown();
			this.closeVendorDropdown();
		},

		handleWindowScroll(evt) {
			const target = evt?.target;
			if (target && target.nodeType === 1) {
				const matEl = this.$refs.materialDropdownEl;
				const supEl = this.$refs.supplierDropdownEl;
				const venEl = this.$refs.vendorDropdownEl;
				if (
					(matEl && matEl.contains(target)) ||
					(supEl && supEl.contains(target)) ||
					(venEl && venEl.contains(target))
				) {
					return;
				}
			}
			this.closeAllDropdowns();
		},

		extractMessage(message) {
			if (!message) return "";
			if (typeof message === "string") return message;
			if (Array.isArray(message)) return String(message[0] ?? "");
			if (typeof message === "object") {
				const first = Object.values(message)[0];
				if (Array.isArray(first)) return String(first[0] ?? "");
				return String(first ?? "");
			}
			return String(message);
		},

		applyFieldErrors(source) {
			if (!source || typeof source !== "object" || Array.isArray(source)) return;
			Object.keys(source).forEach((k) => {
				const v = source[k];
				this.errors[k] = Array.isArray(v) ? v[0] : v;
			});
		},

		handleApiError(errorOrResponse) {
			const data = errorOrResponse?.response?.data ?? errorOrResponse?.data ?? errorOrResponse;
			const fieldErrors = data?.errors;
			const message = data?.message;

			if (fieldErrors && typeof fieldErrors === "object" && !Array.isArray(fieldErrors)) {
				this.applyFieldErrors(fieldErrors);
			}
			if (message && typeof message === "object" && !Array.isArray(message)) {
				this.applyFieldErrors(message);
			}

			return this.extractMessage(message) || this.extractMessage(fieldErrors) || "Something went wrong";
		},

		validate() {
			this.errors = {};
			if (!this.form.product_type) {
				this.errors.product_type = "Select product type";
				return false;
			}

			if (this.form.product_type === "1") {
				if (!this.materialRows.length) {
					this.errors.raw_material_ids = "Add at least one material";
					return false;
				}
				for (const r of this.materialRows) {
					if (!r.material_id) {
						this.errors.raw_material_ids = "Select material for all rows";
						return false;
					}
					const q = parseInt(r.qty, 10);
					if (isNaN(q) || q < 0) {
						this.errors.raw_material_qty = "Enter valid quantity for all rows";
						return false;
					}
					const a = parseFloat(r.amount);
					if (isNaN(a) || a < 0) {
						this.errors.raw_material_price = "Enter valid amount for all rows";
						return false;
					}
				}
			} else if (this.form.product_type === "6") {
				if (!this.productRows.length) {
					this.errors.product_name = "Add at least one product";
					return false;
				}
				for (const r of this.productRows) {
					if (!String(r.product_name || "").trim()) {
						this.errors.product_name = "Enter product name for all rows";
						return false;
					}
					const q = parseInt(r.qty, 10);
					if (isNaN(q) || q < 0) {
						this.errors.product_qty = "Enter valid quantity for all rows";
						return false;
					}
					const p = parseFloat(r.price);
					if (isNaN(p) || p < 0) {
						this.errors.product_price = "Enter valid price for all rows";
						return false;
					}
				}
			} else if (this.form.product_type === "7") {
				if (!this.form.vendor_id) {
					this.errors.vendor_id = "Select vendor";
					return false;
				}
				const count = parseInt(this.form.count, 10);
				if (isNaN(count) || count < 1) {
					this.errors.count = "Enter a valid count (min 1)";
					return false;
				}
			} else {
				const q = parseInt(this.form.product_qty, 10);
				if (isNaN(q) || q < 0) {
					this.errors.product_qty = "Enter a valid quantity";
					return false;
				}
			}

			const amount = parseFloat(this.form.amount);
			if (isNaN(amount) || amount < 0) {
				this.errors.amount = "Enter a valid amount";
				return false;
			}
			const gstRaw = String(this.form.gst_amount || "").trim();
			if (gstRaw !== "") {
				const gst = parseFloat(gstRaw);
				if (isNaN(gst) || gst < 0) {
					this.errors.gst_amount = "Enter a valid GST amount";
					return false;
				}
			}
			const otherParsed = parseFloat(this.form.other_amounts);
			if (isNaN(otherParsed) || otherParsed < 0) {
				this.errors.other_amounts = "Enter a valid other amount";
				return false;
			}
			const roundOffParsed = parseFloat(this.form.round_off_amount);
			if (isNaN(roundOffParsed)) {
				this.errors.round_off_amount = "Enter a valid round off amount";
				return false;
			}
			const total = parseFloat(this.form.total_amount);
			if (isNaN(total) || total < 0) {
				this.errors.total_amount = "Enter a valid total amount";
				return false;
			}

			return true;
		},

		buildPayload() {
			const gstParsed = parseFloat(this.form.gst_amount);
			const otherParsed = parseFloat(this.form.other_amounts);
			const roundOffParsed = parseFloat(this.form.round_off_amount);
			const paidParsed = parseFloat(this.form.paid_amount);
			const payload = {
				product_type: parseInt(this.form.product_type, 10),
				bank_account_id: this.form.bank_account_id ? parseInt(this.form.bank_account_id, 10) : null,
				supplier_id: this.form.product_type === "7" ? null : (this.form.supplier_id ? parseInt(this.form.supplier_id, 10) : null),
				vendor_id: this.form.product_type === "7" && this.form.vendor_id ? parseInt(this.form.vendor_id, 10) : null,
				count: this.form.product_type === "7" ? parseInt(this.form.count, 10) : null,
				amount: parseFloat(this.form.amount),
				gst_amount: isNaN(gstParsed) ? 0 : gstParsed,
				other_amounts: isNaN(otherParsed) ? 0 : otherParsed,
				other_amounts_remarks: String(this.form.other_amounts_remarks || "").trim() || null,
				round_off_amount: isNaN(roundOffParsed) ? 0 : roundOffParsed,
				total_amount: parseFloat(this.form.total_amount),
				payment_id: String(this.form.payment_id || "").trim() || null,
				payment_date: String(this.form.payment_date || "").trim() || null,
				invoice_number: String(this.form.invoice_number || "").trim() || null,
				invoice_date: String(this.form.invoice_date || "").trim() || null,
				remarks: String(this.form.remarks || "").trim() || null,
				user_name: String(this.form.user_name || "").trim() || null,
				user_mobile: String(this.form.user_mobile || "").trim() || null,
				raw_material_ids: null,
				raw_material_qty: null,
				raw_material_price: null,
				product_qty: null,
				product_name: null,
				product_price: null,
				paid_amount: isNaN(paidParsed) ? 0 : paidParsed,
			};

			if (this.form.product_type === "1") {
				payload.raw_material_ids = this.materialRows.map((r) => parseInt(r.material_id, 10));
				payload.raw_material_qty = this.materialRows.map((r) => parseInt(r.qty, 10));
				payload.raw_material_price = this.materialRows.map((r) => parseFloat(r.amount));
			} else if (this.form.product_type === "6") {
				payload.product_name = this.productRows.map((r) => String(r.product_name || "").trim());
				payload.product_qty = this.productRows.map((r) => parseInt(r.qty, 10));
				payload.product_price = this.productRows.map((r) => parseFloat(r.price));
			} else if (this.form.product_type === "7") {
				payload.product_qty = parseInt(this.form.count, 10);
			} else {
				payload.product_qty = parseInt(this.form.product_qty, 10);
			}

			return payload;
		},

		resetAll() {
			this.errors = {};
			this.submitting = false;
			this.materialRows = [];
			this.productRows = [];
			this.supplierSearch = "";
			this.vendorSearch = "";
			this.form = {
				product_type: "",
				bank_account_id: "",
				supplier_id: "",
				vendor_id: "",
				count: "",
				amount: "",
				gst_amount: "",
				other_amounts: 0,
				other_amounts_remarks: "",
				round_off_amount: 0,
				total_amount: "",
				product_qty: "",
				product_name: "",
				payment_id: "",
				payment_date: "",
				invoice_number: "",
				invoice_date: "",
				remarks: "",
				user_name: "",
				user_mobile: "",
				paid_amount: 0,
			};
		},

		async submit() {
			if (this.submitting) return;
			if (!this.validate()) return;
			this.submitting = true;
			this.getToken();
			try {
				const payload = this.buildPayload();
				const res = await this.$axios.post("/staffs/createPurchase", payload, {
					headers: { Authorization: "Bearer " + this.token, "Content-Type": "application/json" },
				});
				const ok = res.data?.status === true || res.data?.status === "true";
				if (ok) {
					this.$swal("Success", this.extractMessage(res.data?.message) || "Purchase created", "success");
					this.resetAll();
					this.fetchPurchaseList();
				} else {
					this.$swal("Error", this.handleApiError(res), "error");
				}
			} catch (err) {
				this.$swal("Error", this.handleApiError(err), "error");
			} finally {
				this.submitting = false;
			}
		},
	},
};
</script>
